import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import "./assets/scss/global.css";
import "./styles/modern-theme.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { disableReactDevTools } from "@fvilers/disable-react-devtools";
import { SidebarProvider } from "./components/ui/sidebar";
import { CartProvider } from "./context/CartContext";
import { ThemeProvider } from "./context/ThemeContext";
import { TasksProvider } from "./context/TasksContext";
import Header from "./cmps/Header";
import PageLoader from "./cmps/PageLoader";
import LanguageSwitchOverlay from "./cmps/LanguageSwitchOverlay";
import FloatingButtons from "./cmps/FloatingButtons";
import { useCart } from "./context/CartContext";
import { useTranslation } from "./services/translation.service";

// Lazy load pages for better performance
const ManualFormPage = lazy(() => import("./views/ManualFormPage"));
const HaatMarket = lazy(() => import("./views/HaatMarket"));
const CartPage = lazy(() => import("./views/CartPage"));
// const ChatPage = lazy(() => import("./views/ChatPage"));
const AuthPage = lazy(() => import("./views/AuthPage"));

if (process.env.REACT_APP_NODE_ENV === "production") {
  disableReactDevTools();
}

function ScrollToTopOnRouteChange() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.key]);

  return null;
}

function ProtectedRoutes() {
  const { otpReady, isAuthInitializing } = useCart();
  const { getDirection } = useTranslation();
  if (isAuthInitializing) return <PageLoader />;
  if (!otpReady) {
    return <Navigate to="/login" replace />;
  }

  return (
    <TasksProvider>
      <div
        className="group/sidebar-wrapper flex w-full min-w-0 bg-gray-50 transition-colors duration-300"
        dir={getDirection()}
      >
        <div className="flex flex-col flex-1 w-full min-w-0">
          <Header />

          <main className="flex-grow w-full min-w-0">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HaatMarket />} />
                <Route path="/manual-form" element={<ManualFormPage />} />
                <Route path="/cart" element={<CartPage />} />
                {/* <Route path="/chat" element={<ChatPage />} /> */}
              </Routes>
            </Suspense>
          </main>
        </div>
        <FloatingButtons />
      </div>
    </TasksProvider>
  );
}

function AuthRoute() {
  const { otpReady, isAuthInitializing } = useCart();
  if (isAuthInitializing) return <PageLoader />;
  if (otpReady) {
    return <Navigate to="/" replace />;
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <AuthPage />
    </Suspense>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTopOnRouteChange />
      <ThemeProvider>
        <CartProvider>
          <SidebarProvider>
            <LanguageSwitchOverlay />
            <AppRoutes />
          </SidebarProvider>
        </CartProvider>
      </ThemeProvider>
    </Router>
  );
}

function AppRoutes() {
  const { isRTL } = useTranslation();
  return (
    <>
      <Routes>
        <Route path="/login" element={<AuthRoute />} />
        <Route path="/*" element={<ProtectedRoutes />} />
      </Routes>
      <ToastContainer
        position="top-center"
        autoClose={2500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={isRTL()}
        style={{ zIndex: 99999 }}
      />
    </>
  );
}

export default App;
